// WasmSimBridge — 11 §6.2 over `fk-sim-wasm` (crates/fk-sim-wasm). Decision C5: main thread,
// synchronous. Snapshots live in wasm memory behind `SnapshotHandle`s; the bridge keeps a TS
// `Snapshot` view per handle for the renderer (`SnapshotAccess`). Every number the intent layer
// draws comes out of `project()`; this file only converts shapes (./adapt.ts).
import type { ProgressRow } from './progression';
import { itemWasmAbilities } from '../items/abilities';
import type { SnapshotHandle as WasmHandle } from 'fk-sim-wasm';
import type { ActionArgs, AimRange, FogView, Intent, LegalityVerdict, ProjectedBoard, ResolutionLog, Slot, Snapshot, StandAt, Survey, Tile } from './types';
import { intentKey } from './types';
import { ProjectStats, type RealmIntent, type RealmPreview, type SimBridge, type SnapshotAccess, type SnapshotHandle } from './SimBridge';
import { aimRangeFromWasm, argsToWasm, routeLegsFromWasm, standsFromWasm, boardFromWasm, fogFromWasm, intentFromWasm, intentToWasm, logFromWasm, mindFromWasm, slotToU8, snapshotFromWasm, surveyFromWasm, verdictFromWasm, type HeroMeta } from './adapt';
import { catalogueFromWasm, type ActionDef, type WasmAbility } from './actions';
import type { Wasm } from './wasm';
import { adventureFromWasm, type Adventure } from '../adventure/graph';
import { consumablesFromWasm, type ConsumableDef, type ItemCard } from './items';

/** docs/112: one upgrade a piece may take — the rank applying it reaches, its price, or why not. */
export interface UpgradeOption { id: number; rank: number; price: number; why?: string | null }
/** What a piece takes (`kinds`: 1 enchant, 2 improve) and every upgrade that fits it. */
export interface UpgradeOptions { kinds: number; options: UpgradeOption[] }

/** `fk_grammar::TierStats`: the flat lines an item adds in `derive_stats` (10 §6.4). */
export interface ItemTierStats {
  base_dmg: number; reach: number; range: number; min_range: number; guard: number; hp: number; move_tiles: number;
  power: number; focus: number; resource_max: number; push_resist: number; guard_pierce: number;
}
/** One item of a starting kit; `slot` is the 10 §6.1 gear slot when worn, null when bagged. The
 *  rest is the catalogue's own numbers (docs/59 CC8): `family` is the serde `ItemFamily`
 *  (`{ Weapon: "Sword" }`, `{ Shield: 1 }`, `{ Armour: "Leather" }`, `"Focus"`, `{ Consumable: 0 }`…),
 *  `stats` the tier's lines (null for a consumable), `use_effect` what Use Item does with a
 *  consumable (`{ Heal: 4 }`, `{ Resource: 2 }`, `{ Hidden: 1 }`, `"Reveal"`). */
export interface KitItem {
  slot: number | null; def: number; tier: number; name: string;
  family?: unknown; two_handed?: boolean; stats?: ItemTierStats | null; use_effect?: unknown; price?: number;
  /** docs/95: a hand-authored piece's powers, as `fk_grammar::Power` serialises (`{ FirstBlow: 2 }`). */
  powers?: unknown[];
}
export interface Background { id: number; name: string; blurb: string; grants: string; gold: number; items: KitItem[] }
export interface Loadout { class: number; id: number; name: string; blurb: string; items: KitItem[] }
/** `background_order[class]`: the background ids in the order the forge offers them to that class;
 *  the first is the one picked when the class is chosen (`fk_grammar::backgrounds_for`). */
export interface Origins { backgrounds: Background[]; loadouts: Loadout[]; background_order: number[][] }
/** `fk_sim::UnitStats` as serde-wasm-bindgen emits it (the fields the creation screen reads). */
export interface PreviewStats {
  hp_max: number; move_tiles: number; reach: number; range: number; guard: number; power: number; focus: number;
  initiative: number; capability: number; weapon_damage: number; ranged_weapon: boolean; has_shield: boolean;
  abilities: number[]; resource: { kind: string; max: number; current: number; regen: number };
}
export interface HeroPreview { stats: PreviewStats; kit: KitItem[]; /** docs/59 CC6: the ranger's companion at this level. */ companion?: PreviewStats | null }

/** docs/59: `fk_grammar::CallingDef` — a feat, specialty, patron or companion. */
export interface CallingDef {
  id: number; kind: 'Feat' | 'Specialty' | 'Patron' | 'Companion' | 'Oath' | 'Circle' | 'Instrument'; class: string; name: string; title: string;
  blurb: string; rules: string; grants: number[];
  stats: { hp: number; guard: number; move_tiles: number; power: number; focus: number; resource_max: number };
}
/** docs/59 CC6: `fk_grammar::CompanionDef` — the beast's body at ranger level 1. */
export interface CompanionDef {
  calling: number; hp: number; guard: number; move_tiles: number; bite: number;
  trick: number; frame: number; gait: 'Beast' | 'Flier' | 'Swimmer'; senses: string;
  /** The colours a ranger may choose; the first is `frame`. */
  coats: { name: string; frame: number }[];
}
/** docs/59: the `callings()` export — every creation choice beside background and loadout. */
export interface Callings {
  species: { id: number; name: string }[];
  callings: CallingDef[];
  companions: CompanionDef[];
  wizard_spells: number[]; wizard_picks: number; default_wizard_spells: number[];
  pet_name_max: number; companion_hp_per_level: number; frenzy: number;
}

/** docs/47: one ability of the spellbook page. */
export interface SpellbookEntry { id: number; name: string; class: string | null; level: number; slot: string; sounded: boolean }
/** docs/53: one move of the foes' vocabulary, for the move book page. */
export interface MoveEntry {
  id: number; key: string; name: string; words: string; after: string;
  form: 'villain' | 'signature' | 'bonus' | 'reaction' | 'bloodied'; slots: number[];
  cue: string; bank: string; kins: string[];
}
/** docs/140 DT12: a demo trap's run (`snare_demo_run`), shaped for the playback. */
export interface SnareDemoRun {
  victim: number; foe?: number; heroes: number[]; start: Snapshot;
  frames: Array<{ pulse: number; log: ResolutionLog; snapshot: Snapshot; cues: SfxCue[] }>;
}
/** docs/47: a cast on the stage — its log and the cues the module's director scores for it. */
export interface StagedCast { log: ResolutionLog; cues: SfxCue[]; catalogue: number }
/** A cue in the shape of the `sfx_score` row's `SfxCueRow` (camelCase, as the bindings decode it). */
export interface SfxCue { bank: string; variant: number; anchor: number; seq: number; beat: number; step: number; delayMs: number; gainBp: number; rateBp: number; unit: number | undefined }

/** The stage's party: the P0 four and Wren (fk_sim::spellbook::sheets). */
/** docs/55, docs/57: `fk-sim-wasm::puzzle_card`. */
export interface PuzzleCard {
  place: string; scene: string; ground: string; kit: string; hour: number; indoors: boolean;
  hall: string; stages: string[]; twists: string[]; genre: string; rules: string[]; lockRule: string;
  pieces: string[]; says: string[]; clues: string[]; answer: string; par: number; steps: string[];
  states: number; attempt: number; proven: boolean; w: number; h: number;
}

/** docs/60: `fk-sim-wasm::crawl_card` — what the `?crawl=1` gallery says about a forged crawl (the riddle's answer included). */
export interface CrawlCardInfo {
  name: string; premise: string; hook: string; ground: string; kit: string; indoors: boolean;
  depths: string[]; dweller: string; denizens: string[]; wanderer: string | null; shrine: string | null;
  lore: string[]; riddle: string | null; features: string[];
  w: number; h: number; chambers: number; lairs: number; roamers: number; foes: number; route: number; estPulses: number; attempt: number; ascii: string;
}

/** docs/61: `fk-sim-wasm::explore_card` — what the `?explore=1` gallery says about a forged exploration. */
export interface ExploreCardInfo {
  name: string; premise: string; hook: string; ground: string; kit: string; indoors: boolean; air: string;
  reaches: string[]; wonders: string[]; guardians: string[]; ways: string[]; features: string[];
  w: number; h: number; places: number; estPulses: number; attempt: number; ascii: string;
}

const STAGE_META = new Map<number, HeroMeta>([[1, { shapeMark: 1, classId: 'Fighter' }], [2, { shapeMark: 2, classId: 'Wizard' }], [3, { shapeMark: 3, classId: 'Cleric' }], [4, { shapeMark: 4, classId: 'Ranger' }], [5, { shapeMark: 5, classId: 'Rogue' }],
  // docs/104: the spellbook stage's paladin, druid and bard (fk_sim::spellbook::sheets)
  [6, { shapeMark: 6, classId: 'Paladin' }], [7, { shapeMark: 7, classId: 'Druid' }], [8, { shapeMark: 8, classId: 'Bard' }]]);

function castFromWasm(v: unknown): StagedCast {
  const o = v as { log: Record<string, unknown>; cues: Array<SfxCue & { unit: number | null }>; catalogue: number };
  return { log: logFromWasm(o.log), cues: o.cues.map((c) => ({ ...c, unit: c.unit ?? undefined })), catalogue: o.catalogue };
}

const perf = (): number => (typeof performance !== 'undefined' ? performance.now() : 0);

interface Entry { h: WasmHandle; snapshot: Snapshot; lastIntents: Intent[] }

export interface MadeIntent { intent: Intent; bytes: Uint8Array }

export class WasmSimBridge implements SimBridge, SnapshotAccess {
  readonly rulesetVersion: number;
  private handles = new Map<number, Entry>();
  private nextId = 1;
  private readonly stat = new ProjectStats();
  /** Hero id → owner identity hex, for `ProjectedUnit.owner`. */
  owners = new Map<number, string>();

  constructor(private readonly w: Wasm) { this.rulesetVersion = w.ruleset_version(); }

  // ---- snapshots ------------------------------------------------------------------------------

  /** The `SimBridge` signature; the declaration blob and hero metadata go through `load`. */
  async loadSnapshot(bytes: Uint8Array): Promise<SnapshotHandle> { return this.load(bytes, new Uint8Array(), new Map()); }

  /**
   * `hash`: the server's hash for this snapshot. Under fog of war (docs/31) the row carries the
   * party's view, whose bytes hash differently from the truth the server checks intents against.
   */
  load(snapshot: Uint8Array, declaration: Uint8Array, meta: Map<number, HeroMeta>, hash?: string): SnapshotHandle {
    const h = this.w.load_snapshot(snapshot, declaration);
    return this.register(h, meta, hash);
  }

  /** The offline Mirewood fixture (`demo_encounter`) for tests and the e2e smoke. */
  loadDemo(encounterId: number, meta: Map<number, HeroMeta> = new Map()): SnapshotHandle {
    return this.register(this.w.demo_encounter(BigInt(encounterId)), meta);
  }

  private register(h: WasmHandle, meta: Map<number, HeroMeta>, serverHash?: string): SnapshotHandle {
    // the wasm handle then makes intents with the server's hash and accepts it in `legal`
    const own = this.w.snapshot_hash(h);
    if (serverHash) this.w.set_served_hash(h, serverHash);
    const hash = this.w.snapshot_hash(h);
    const snapshot = snapshotFromWasm(this.w.snapshot_view(h) as Record<string, unknown>, hash, meta);
    // docs/52 HT3: the bytes are the party's view whenever they hash differently from the truth
    // (fog, a lock's answer, a hidden thing or a disguised trap struck out)
    if (serverHash && serverHash !== own) snapshot.partyView = true;
    // docs/30: every enemy's mind in the sim's words, for the foe list and its inspect card
    snapshot.minds = ((this.w.minds(h) as Record<string, unknown>[]) ?? []).map(mindFromWasm);
    // docs/31: the fog of war as the party sees it
    const fog = fogFromWasm(this.w.fog_view(h));
    // docs/118 PT12: in a crawl what was never seen is no board (`blindFog`)
    if (fog) snapshot.fog = snapshot.crawl ? { ...fog, blind: true } : fog;
    const id = this.nextId++;
    this.handles.set(id, { h, snapshot, lastIntents: [] });
    for (const [heroId, m] of meta) if (m.owner) this.owners.set(heroId, m.owner);
    return { id, hash };
  }

  free(handle: SnapshotHandle): void {
    const e = this.handles.get(handle.id);
    if (!e) return;
    e.h.free();
    this.handles.delete(handle.id);
  }

  snapshotFor(h: SnapshotHandle): Snapshot | undefined { return this.handles.get(h.id)?.snapshot; }

  /**
   * docs/31: the party's fog part-way through a pulse's playback — the board `start` began on, with
   * the `changed` squares (row-major) as they stand on `end`, and the party where `at` puts it.
   */
  fogBetween(start: SnapshotHandle, end: SnapshotHandle, changed: number[], at: { unit: number; tile: Tile; down: boolean }[]): FogView | undefined {
    const e = this.entry(start);
    const fog = fogFromWasm(this.w.fog_view_between(e.h, this.entry(end).h, Uint32Array.from(changed), at));
    // docs/118 PT12: a crawl's fog stays blind through the playback
    return fog && e.snapshot.crawl ? { ...fog, blind: true } : fog;
  }

  private entry(h: SnapshotHandle): Entry {
    const e = this.handles.get(h.id);
    if (!e) throw new Error(`stale snapshot handle ${h.id}`);
    return e;
  }

  private setIntents(e: Entry, intents: Intent[]): void {
    this.w.set_intents(e.h, intents.map(intentToWasm));
    e.lastIntents = intents;
  }

  // ---- 04 §5 ----------------------------------------------------------------------------------

  /** `legal()` against the current intent set minus the candidate's own (hero, slot) (04 §5.2). */
  legal(h: SnapshotHandle, intent: Intent): LegalityVerdict {
    const e = this.entry(h);
    const key = intentKey(intent);
    const others = e.lastIntents.filter((i) => intentKey(i) !== key);
    if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, others.map(intentToWasm));
    try {
      return verdictFromWasm(this.w.legal(e.h, intentToWasm(intent)));
    } catch (err) {
      return { legal: false, reason: err instanceof Error ? err.message : String(err) };
    } finally {
      if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, e.lastIntents.map(intentToWasm));
    }
  }

  /**
   * docs/118 PT13: `legal()` for many candidates of one (hero, slot) — the same verdicts, but the
   * plan is run once rather than once per candidate (a run per square froze a big crawl for ~10 s).
   */
  legalMany(h: SnapshotHandle, intents: Intent[]): LegalityVerdict[] {
    if (!intents.length) return [];
    const e = this.entry(h);
    const keys = new Set(intents.map(intentKey));
    const others = e.lastIntents.filter((i) => !keys.has(intentKey(i)));
    if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, others.map(intentToWasm));
    try {
      return (this.w.legal_many(e.h, intents.map(intentToWasm)) as unknown[]).map(verdictFromWasm);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      return intents.map(() => ({ legal: false, reason }));
    } finally {
      if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, e.lastIntents.map(intentToWasm));
    }
  }

  /**
   * docs/118 PT13: the squares a square-aimed `base` may be aimed at, from `among` or (none given)
   * from the squares the action could ever land on — a teleport's or a walk's box round where the
   * hero will stand. One run of the plan, then the sim's per-square checks.
   */
  legalTiles(h: SnapshotHandle, base: Intent, among?: Tile[]): Tile[] {
    const e = this.entry(h);
    const key = intentKey(base);
    const others = e.lastIntents.filter((i) => intentKey(i) !== key);
    if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, others.map(intentToWasm));
    try {
      const flat = this.w.legal_tiles(e.h, intentToWasm(base), among ? Int32Array.from(among.flatMap((t) => [t.x, t.y])) : undefined);
      const out: Tile[] = [];
      for (let i = 0; i + 1 < flat.length; i += 2) out.push({ x: flat[i]!, y: flat[i + 1]! });
      return out;
    } catch {
      return [];
    } finally {
      if (others.length !== e.lastIntents.length) this.w.set_intents(e.h, e.lastIntents.map(intentToWasm));
    }
  }

  /** docs/33 E1: `legal()` as if `before` were planned too (a Move a click would add ahead of an attack). */
  legalWith(h: SnapshotHandle, intent: Intent, before: Intent[]): LegalityVerdict {
    const e = this.entry(h);
    const drop = new Set([intentKey(intent), ...before.map(intentKey)]);
    this.w.set_intents(e.h, [...e.lastIntents.filter((i) => !drop.has(intentKey(i))), ...before].map(intentToWasm));
    try {
      return verdictFromWasm(this.w.legal(e.h, intentToWasm(intent)));
    } catch (err) {
      return { legal: false, reason: err instanceof Error ? err.message : String(err) };
    } finally {
      this.w.set_intents(e.h, e.lastIntents.map(intentToWasm));
    }
  }

  /**
   * The field for one unit at its slot (docs/26 F8): reach with routes, the zones gripping it,
   * sight and the ground legend — the board as it will be when that intent executes, judged by
   * the same walker `legal()` uses.
   */
  survey(h: SnapshotHandle, unitId: number, slot: Slot, intents?: Intent[]): Survey | undefined {
    const e = this.entry(h);
    if (intents) this.setIntents(e, intents);
    try {
      return surveyFromWasm(this.w.survey(e.h, unitId, slot === 1 ? 'One' : slot === 2 ? 'Two' : 'Bonus'));
    } catch {
      return undefined;
    }
  }

  /** docs/48 AR1: an action's range for one unit at its slot, judged where `legal()` judges it. */
  aim(h: SnapshotHandle, unitId: number, slot: Slot, action: number): AimRange | undefined {
    const e = this.entry(h);
    try {
      return aimRangeFromWasm(this.w.aim(e.h, unitId, slot === 1 ? 'One' : slot === 2 ? 'Two' : 'Bonus', action));
    } catch {
      return undefined;
    }
  }

  /** docs/149 MP: the squares a walk to `to` takes in each pulse from now (leg 0 this pulse); `undefined` if it cannot be walked. */
  routeLegs(h: SnapshotHandle, unitId: number, to: Tile): Tile[][] | undefined {
    const e = this.entry(h);
    try {
      return routeLegsFromWasm(this.w.route_legs(e.h, unitId, to.x, to.y));
    } catch {
      return undefined;
    }
  }

  /** docs/81 SA3: every unit's square as this unit's slot begins — friends' ghosts, foes already dropped. */
  stands(h: SnapshotHandle, unitId: number, slot: Slot, intents?: Intent[]): StandAt[] {
    const e = this.entry(h);
    if (intents) this.setIntents(e, intents);
    try {
      return standsFromWasm(this.w.stands(e.h, unitId, slot === 1 ? 'One' : slot === 2 ? 'Two' : 'Bonus'));
    } catch {
      return [];
    }
  }

  project(h: SnapshotHandle, intents: Intent[]): ProjectedBoard {
    const t0 = perf();
    const e = this.entry(h);
    this.setIntents(e, intents);
    const board = boardFromWasm(this.w.project(e.h) as Record<string, unknown>, e.snapshot, this.owners);
    this.stat.record(perf() - t0);
    return board;
  }

  /** docs/33 E1: the projection of `intents` without disturbing the set `legal()` reads (a hover preview). */
  projectAside(h: SnapshotHandle, intents: Intent[]): ProjectedBoard {
    const e = this.entry(h);
    this.w.set_intents(e.h, intents.map(intentToWasm));
    try {
      return boardFromWasm(this.w.project(e.h) as Record<string, unknown>, e.snapshot, this.owners);
    } finally {
      this.w.set_intents(e.h, e.lastIntents.map(intentToWasm));
    }
  }

  planHash(h: SnapshotHandle, intents: Intent[]): string {
    const e = this.entry(h);
    this.setIntents(e, intents);
    return this.w.plan_hash(e.h);
  }

  /** Dev parity (11 §6.4): the full resolution the server would compute for these intents. */
  resolveForParity(h: SnapshotHandle, intents: Intent[], _stream: Uint8Array = new Uint8Array()): ResolutionLog {
    const e = this.entry(h);
    this.setIntents(e, intents);
    return logFromWasm(this.w.resolve(e.h) as Record<string, unknown>);
  }

  /** Build an intent with the handle's snapshot hash filled in, plus the postcard bytes for `submit_intent`. */
  makeIntent(h: SnapshotHandle, heroId: number, slot: Slot, action: number, args: ActionArgs, revision: number): MadeIntent {
    const e = this.entry(h);
    const a = args.kind === 'none' ? null : argsToWasm(args);
    const out = this.w.make_intent(e.h, BigInt(heroId), slotToU8(slot), action, a, revision) as { intent: Record<string, unknown>; bytes: number[] | Uint8Array };
    return { intent: intentFromWasm(out.intent), bytes: out.bytes instanceof Uint8Array ? out.bytes : Uint8Array.from(out.bytes) };
  }

  decodeIntent(bytes: Uint8Array): Intent { return intentFromWasm(this.w.decode_intent(bytes) as Record<string, unknown>); }
  decodeLog(bytes: Uint8Array): ResolutionLog { return logFromWasm(this.w.decode_log(bytes) as Record<string, unknown>); }

  abilities(): ActionDef[] {
    // docs/72 IT18: the catalogue's actives and fight uses join the heroes' table (the wasm lists only the latter)
    const heroes = this.w.abilities() as WasmAbility[];
    const have = new Set(heroes.map((a) => a.id));
    const defs = catalogueFromWasm([...heroes, ...itemWasmAbilities().filter((a) => !have.has(a.id))]);
    // docs/115 ME5: a flask or an elemental spell may be thrown at bare ground as well as at a foe
    const ground = new Set(this.w.ground_aims() as number[]);
    return defs.map((d) => (ground.has(d.id) && d.args === 'unit' ? { ...d, ground: true } : d));
  }

  // ---- docs/47: the spellbook page -------------------------------------------------------------

  /** Every class ability the spellbook shows. */
  spellbook(): SpellbookEntry[] { return this.w.spellbook() as SpellbookEntry[]; }
  /** The stage for `ability`: its caster, friends and a band of goblins, the cast planned. */
  spellStage(ability: number, encounterId: number): SnapshotHandle { return this.register(this.w.spell_stage(ability, BigInt(encounterId)), STAGE_META); }
  /** The stage with the band rehosted as `kin`'s people. */
  foeStage(kin: string, encounterId: number): SnapshotHandle { return this.register(this.w.foe_stage(kin, BigInt(encounterId)), STAGE_META); }
  /** The stage's cast: the party's half of the pulse and the director's cues for it. */
  /** docs/103: the flank book's stage — Wren, a goblin and Jo on an open field. */
  flankStage(encounterId: number, misdirection: boolean): SnapshotHandle { return this.register(this.w.flank_stage(BigInt(encounterId), misdirection), STAGE_META); }
  // ---- docs/140: the trap book (`?snares=1`) -----------------------------------------------------
  /**
   * docs/140 DT12: book entry `def` played on its demo crawl — the board before it springs, then a
   * frame for the spring and one per pulse after it (its entries as a log, the board after, the
   * cues the module's director scores), until it is spent or `pulses` have passed.
   */
  snareDemoRun(def: number, heroes: number, pulses = 40, encounterId = 0): SnareDemoRun {
    const o = this.w.snare_demo_run(def, heroes, pulses) as { victim: number; foe: number | null; heroes: number[]; start: Record<string, unknown>; frames: Array<{ pulse: number; entries: unknown[]; snapshot: Record<string, unknown>; cues: Array<SfxCue & { unit: number | null }> }> };
    const snap = (v: Record<string, unknown>, i: number): Snapshot => { const s = snapshotFromWasm(v, `snare-${def}-${i}`, STAGE_META); if (encounterId) s.encounterId = encounterId; return s; };
    const start = snap(o.start, 0);
    return {
      victim: o.victim, ...(o.foe !== null && o.foe !== undefined ? { foe: o.foe } : {}), heroes: o.heroes, start,
      frames: o.frames.map((f, i) => ({
        pulse: f.pulse,
        log: logFromWasm({ encounter_id: encounterId, pulse_index: f.pulse, ruleset_version: this.rulesetVersion, snapshot_hash: [], plan_hash: [], end_snapshot_hash: [], entries: f.entries, unresolved: [], unready: [] }),
        snapshot: snap(f.snapshot, i + 1),
        cues: f.cues.map((c) => ({ ...c, unit: c.unit ?? undefined })),
      })),
    };
  }

  // ---- docs/114: the matter book (`?matter=1`) --------------------------------------------------
  /** The matter book's scenes, `[key, title]`. */
  matterScenes(): [string, string][] { return this.w.matter_scenes() as [string, string][]; }
  /** The stage for matter scene `key`, its cast planned. */
  matterStage(key: string, encounterId: number): SnapshotHandle { return this.register(this.w.matter_stage(key, BigInt(encounterId)), STAGE_META); }
  /** The intents a staged handle carries (its planned cast). */
  intentsOf(h: SnapshotHandle): Intent[] { return ((this.w.handle_intents(this.entry(h).h) as Record<string, unknown>[]) ?? []).map(intentFromWasm); }
  spellCast(h: SnapshotHandle, seed: number): StagedCast { return castFromWasm(this.w.spell_cast(this.entry(h).h, seed)); }
  /** The band's first foe casting `kind` (`bolt`, `burst`, `curse`, `mend`). */
  foeCast(h: SnapshotHandle, kind: string, seed: number): StagedCast { return castFromWasm(this.w.foe_cast(this.entry(h).h, kind, seed)); }
  // ---- docs/55: the puzzle-room gallery ----------------------------------------------------------

  /** docs/57: a forged puzzle room grown from `vision` (a `PuzzleVision` object) for a party, `pulses`
   *  of its proven solution played; `fogged` begins docs/63's fog of closed doors and holds the party's view. */
  puzzleStage(seed: number, vision: unknown, heroes: number, pulses: number, fogged = false): SnapshotHandle {
    return this.register(this.w.puzzle_stage(BigInt(seed), vision, heroes, pulses, fogged), STAGE_META);
  }
  /** What the gallery's card says about that room (its answer included: a design tool). */
  puzzleCard(seed: number, vision: unknown, heroes: number): PuzzleCard { return this.w.puzzle_card(BigInt(seed), vision, heroes) as PuzzleCard; }
  /** docs/57: the forge's own vision on ground `ground` (`PuzzleGround::ALL` order), as the module
   *  draws one where no storyteller wrote it. */
  puzzleTemplateVision(seed: number, ground: number): unknown { return this.w.puzzle_template_vision(BigInt(seed), ground); }
  // ---- docs/60: the dungeon crawl gallery ---------------------------------------------------------

  /** How many themed banks template crawl visions are drawn from. */
  crawlThemeCount(): number { return this.w.crawl_theme_count(); }
  /** The forge's own crawl vision from theme `theme`, as the module draws one where no storyteller wrote it. */
  crawlTemplateVision(seed: number, theme: number): unknown { return this.w.crawl_template_vision(BigInt(seed), theme); }
  /** A forged crawl for `heroes` heroes played by `players` people (0: one each, docs/85); `fogged` begins the fog as the module does (the handle holds the truth). */
  crawlStage(seed: number, vision: unknown, heroes: number, fogged: boolean, players = 0): SnapshotHandle {
    return this.register(this.w.crawl_stage(BigInt(seed), vision, heroes, players, fogged), STAGE_META);
  }
  // ---- docs/61: the exploration gallery -----------------------------------------------------------


  /** How many airs template exploration visions can be drawn in. */
  exploreAirCount(): number { return this.w.explore_air_count(); }
  /** The forge's own exploration vision in air `air` (255: any), as the module draws one where no storyteller wrote it. */
  exploreTemplateVision(seed: number, air: number): unknown { return this.w.explore_template_vision(BigInt(seed), air); }
  /** A forged exploration for `heroes` heroes played by `players` people (0: one each, docs/85); `fogged` begins the fog as the module does (the handle holds the truth). */
  exploreStage(seed: number, vision: unknown, heroes: number, fogged: boolean, players = 0): SnapshotHandle {
    return this.register(this.w.explore_stage(BigInt(seed), vision, heroes, players, fogged), STAGE_META);
  }
  /** What the gallery's card says about the exploration `exploreStage` draws. */
  exploreCard(seed: number, vision: unknown, heroes: number, players = 0): ExploreCardInfo { return this.w.explore_card(BigInt(seed), vision, heroes, players) as ExploreCardInfo; }

  // ---- docs/76: the hidden things gallery ------------------------------------------------------
  /** A staged fight with every hidden thing planted; `reveal` 0 fresh, 1 all found, 2 found and every hidden door open (the party's view). */
  /** The region kit a ground's fields are drawn in. */
  groundKit(ground: string): string { return this.w.ground_kit(ground); }
  secretsStage(ground: string, heroes: number, seed: number, reveal: number): SnapshotHandle {
    return this.register(this.w.secrets_stage(ground, heroes, BigInt(seed), reveal), STAGE_META);
  }
  /** What the gallery's card says about the crawl `crawlStage` draws. */
  crawlCard(seed: number, vision: unknown, heroes: number, players = 0): CrawlCardInfo { return this.w.crawl_card(BigInt(seed), vision, heroes, players) as CrawlCardInfo; }
  // ---- docs/53: the move book page --------------------------------------------------------------

  /** Every move of the foes' vocabulary, in id order. */
  moveBook(): MoveEntry[] { return this.w.move_book() as MoveEntry[]; }
  /** The stage for move `id`, its boss dressed as `kin` (or the first people it fits). */
  moveStage(id: number, kin: string, encounterId: number): SnapshotHandle { return this.register(this.w.move_stage(id, kin, BigInt(encounterId)), STAGE_META); }
  /** The boss performing move `id` on the engine, and the director's cues for it. */
  moveCast(h: SnapshotHandle, id: number, seed: number): StagedCast { return castFromWasm(this.w.move_cast(this.entry(h).h, id, seed)); }
  /** The consumables Use Item spends (docs/40), from the catalogue. */
  consumables(): ConsumableDef[] { return consumablesFromWasm(this.w.consumables() as Parameters<typeof consumablesFromWasm>[0]); }
  /** One item at one tier as a shop shows it (slot, classes, stats, trait), or undefined. */
  itemCard(def: number, tier: number): ItemCard | undefined { return (this.w.item_card(def, tier) as ItemCard | null) ?? undefined; }
  /** `adventure_instance.blob` → the site graph and outcome table (11 §3.1 C2). */
  decodeAdventure(bytes: Uint8Array): Adventure { return adventureFromWasm(this.w.decode_adventure(bytes) as Record<string, unknown>); }
  /** docs/113: a library quest walked as a path, in `scene` (or its own words' scene), for the `?path=1` gallery. */
  previewQuestPath(seed: number, main: boolean, scene: string): Adventure { return adventureFromWasm(this.w.preview_quest_path(seed, main, scene) as Record<string, unknown>); }
  /** Backgrounds and loadouts (`fk_grammar::origins`, docs/17 §4); fixed per ruleset, so cached. */
  origins(): Origins { return (this.originsCache ??= this.w.origins() as Origins); }
  private originsCache?: Origins;
  /** docs/59: species, feats, specialties, patrons, companions and the wizard's spell list. */
  callings(): Callings { return (this.callingsCache ??= this.w.callings() as Callings); }
  private callingsCache?: Callings;
  /** `derive_stats` over the exact kit `forge_hero` would grant, with the calling chosen
   *  (docs/59: `pick` a calling id, `spells` a wizard's three; blank reads as before). */
  previewHero(cls: number, species: number, build: number, background: number, loadout: number, level = 1, pick = 0, spells: readonly number[] = []): HeroPreview {
    return this.w.preview_hero(cls, species, build, background, loadout, level, pick, Uint16Array.from(spells)) as HeroPreview;
  }

  /** docs/71 INV8: `derive_stats` for a hero wearing exactly `worn` (`[slot, def, tier, …]`), with its level cards and calling. */
  /** docs/112: which upgrades fit a piece of kind `def` carrying `applied`, each with the rank and price
   *  applying it would mean (or why it cannot), and what kinds the piece takes (1 enchant, 2 improve). */
  upgradeOptions(def: number, applied: readonly number[]): UpgradeOptions {
    return this.w.upgrade_options(def, Uint16Array.from(applied)) as UpgradeOptions;
  }

  /** docs/112: `upgrades` is each worn triple's upgrades in order, `[n, id × n, …]` (empty: none). */
  previewHeroGear(cls: number, species: number, build: number, level: number, pick: number, spells: readonly number[], cards: readonly number[], worn: readonly number[], upgrades: readonly number[] = [], progress?: ProgressRow): PreviewStats {
    // docs/119: with what the hero made of their levels
    return this.w.preview_hero_gear(cls, species, build, level, pick, Uint16Array.from(spells), Uint16Array.from(cards), Uint16Array.from(worn), Uint16Array.from(upgrades),
      Uint16Array.from(progress?.picks ?? []), Uint8Array.from(progress?.training ?? []), Uint16Array.from(progress?.talents ?? []), Uint16Array.from(progress?.laurels ?? [])) as PreviewStats;
  }

  realmPreview(_state: Uint8Array, _intents: RealmIntent[]): RealmPreview { return { lines: ['realm preview: not wired in P0'] }; }
  stats() { return this.stat.snapshot(); }
}
